import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store/useStore';

export default function SearchBar() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { setSearchQuery } = useStore();

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Global keyboard shortcut: "/" to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // "/" key to open search
      if (e.key === '/') {
        e.preventDefault();
        setQuery(''); // Clear previous query
        setIsExpanded(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      const searchTerm = query.trim();
      setSearchQuery(searchTerm);
      setQuery(''); // Clear query so it's ready for next search
      setIsExpanded(false);
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleBlur = () => {
    if (!query) {
      setIsExpanded(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="relative">
      <div
        className={`flex items-center transition-all duration-300 ${
          isExpanded ? 'bg-black/80 border border-white/30' : 'bg-transparent'
        } min-h-10 rounded-full overflow-hidden`}
      >
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex min-h-10 items-center gap-1.5 px-3 py-2 leading-none transition-colors hover:text-gray-300"
          aria-label="Search"
          title="Search (press /)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {!isExpanded && (
            <span className="hidden sm:inline-flex items-center text-xs text-gray-500 bg-white/10 px-1.5 py-0.5 rounded">/</span>
          )}
        </button>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onBlur={handleBlur}
          placeholder="Field trip search: titles, people, genres"
          className={`bg-transparent outline-none text-sm transition-all duration-300 ${
            isExpanded ? 'w-48 sm:w-64 px-2 py-2 leading-none' : 'w-0 px-0'
          }`}
        />

        {isExpanded && query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="min-h-10 px-3 py-2 transition-colors hover:text-gray-300"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </form>
  );
}
